import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TRANSLATIONS } from "./translations";

const LanguageContext = createContext(null);
const STORAGE_KEY = "chigle_lang";

function detectInitial() {
    // English is always the default. We only switch to Hindi if the user explicitly
    // chose it before (URL prefix /hi or a previously-stored choice in localStorage).
    // We deliberately do NOT auto-detect from navigator.language — keeping EN as default.
    if (typeof window === "undefined") return "en";
    if (window.location.pathname.startsWith("/hi")) return "hi";
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === "hi") return "hi";
    } catch {}
    return "en";
}

function stripHiPrefix(pathname) {
    if (pathname === "/hi") return "/";
    if (pathname.startsWith("/hi/")) return pathname.replace(/^\/hi/, "");
    return pathname;
}
function addHiPrefix(pathname) {
    if (pathname === "/") return "/hi";
    return `/hi${pathname}`;
}

export function LanguageProvider({ children }) {
    const location = useLocation();
    const navigate = useNavigate();

    // The URL is the source of truth for language; localStorage only persists user choice.
    const lang = location.pathname.startsWith("/hi") ? "hi" : "en";

    // On first mount, redirect to preferred language if URL doesn't match
    useEffect(() => {
        const preferred = detectInitial();
        if (preferred !== lang) {
            const target =
                preferred === "hi"
                    ? addHiPrefix(location.pathname)
                    : stripHiPrefix(location.pathname);
            navigate(target + location.hash, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reflect lang in <html lang="...">
    useEffect(() => {
        document.documentElement.lang = lang === "hi" ? "hi" : "en";
    }, [lang]);

    const setLang = useCallback(
        (next) => {
            try {
                localStorage.setItem(STORAGE_KEY, next);
            } catch {}
            const target =
                next === "hi"
                    ? addHiPrefix(stripHiPrefix(location.pathname))
                    : stripHiPrefix(location.pathname);
            navigate(target + location.hash);
        },
        [location.pathname, location.hash, navigate]
    );

    const t = useCallback(
        (key, fallback) => {
            const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
            return dict[key] ?? TRANSLATIONS.en[key] ?? fallback ?? key;
        },
        [lang]
    );

    const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useT() {
    const ctx = useContext(LanguageContext);
    if (!ctx) {
        // Fallback for components rendered outside provider (shouldn't happen)
        return { lang: "en", setLang: () => {}, t: (k) => TRANSLATIONS.en[k] ?? k };
    }
    return ctx;
}
